(() => {
  const root = document.documentElement;
  const body = document.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (
    !body?.hasAttribute('data-briefbuilt-motion') ||
    reduceMotion.matches ||
    !('IntersectionObserver' in window) ||
    typeof Element.prototype.animate !== 'function'
  ) {
    return;
  }

  const activeAnimations = new Set();
  const observedElements = new Set();
  const handledElements = new Set();
  const firstViewportBoundary = Math.max(0, window.innerHeight * 0.92);

  const groups = [
    // Keep the complete first viewport immediately visible. Motion starts below
    // the hero so the main promise and actions never wait on JavaScript.
    {
      selector: '.process-card',
      distance: 24,
      duration: 560,
      stagger: 85
    },
    {
      selector: '.package',
      distance: 24,
      duration: 560,
      stagger: 85
    },
    {
      selector: '.add-on',
      distance: 24,
      duration: 560,
      stagger: 85
    },
    {
      selector: '.store-preview__included li',
      distance: 16,
      duration: 480,
      stagger: 65
    },
    {
      selector: '.store-preview__steps li',
      distance: 16,
      duration: 480,
      stagger: 65
    },
    {
      selector: '.about-brand__values > span',
      distance: 16,
      duration: 480,
      stagger: 65
    },
    {
      selector: '.faq__item',
      distance: 14,
      duration: 440,
      stagger: 40
    },
    {
      selector: '.disclosure__grid article',
      distance: 14,
      duration: 440,
      stagger: 40
    },
    {
      selector: '.product__media, .product__info',
      distance: 20,
      duration: 580,
      stagger: 90
    },
    {
      selector: '.package-timeline__steps > *, .product-add-on',
      distance: 16,
      duration: 480,
      stagger: 55
    },
    {
      selector: '.page-main__intro, .page-main__jumps',
      distance: 18,
      duration: 520,
      stagger: 70
    },
    {
      selector: '.page-main > .rte > h2, .page-main > .rte > h3, .page-main > .rte > p, .page-main > .rte > ul, .page-main > .rte > ol, .page-main > .rte > blockquote, .page-main > .rte > table, .page-main > .rte > div',
      distance: 26,
      duration: 640,
      stagger: 55
    },
    {
      selector: '.collection__link, .blog-card',
      distance: 20,
      duration: 520,
      stagger: 65
    },
    {
      selector: '.blog-main__header, .contact__grid > *, .error-page__inner > *, .secure-feedback-notice__card',
      distance: 18,
      duration: 540,
      stagger: 70
    },
    {
      selector: '.article-main__hero-copy, .article-main__visual, .article-main__toc, .article-main__content > h2, .article-main__content > h3, .article-main__content > ul, .article-main__content > ol, .article-main__content > blockquote, .article-main__content > table, .article-main__aside, .article-main__footer',
      distance: 18,
      duration: 620,
      stagger: 55
    },
    {
      selector: '.entity-page__main, .entity-page__aside',
      distance: 20,
      duration: 560,
      stagger: 90
    },
    {
      selector: '.traffic-landing__hero > *, .traffic-landing__detail, .traffic-landing__steps > *, .traffic-landing__packages > *, .traffic-landing__questions > *, .traffic-landing__final',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.resource-hub__header, .resource-hub__groups > *, .resource-hub__cards > *, .resource-hub__cta',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.decision-resource__inner > *, .decision-resource-faq__item',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.dropship-guide__inner > *, .methodology-page__inner > *, .readiness-tool__header, .readiness-tool__form, .readiness-tool__result, .readiness-tool__sources',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.case-study__inner > *, .case-study__facts > *, .case-study__sections > *, .case-study__cta',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.seo-intent-links__grid > *, .work-card, .testimonials__grid > *',
      distance: 18,
      duration: 500,
      stagger: 60
    },
    {
      selector: '.search-main__form, .search-main__results-head, .search-result, .search-main__empty > *, .search-main__starts > *',
      distance: 28,
      duration: 660,
      stagger: 70
    },
    {
      selector: '.cart-items, .cart-summary, .cart-support, .cart-soft-launch, .empty-state, .cart-warning, .cart-next',
      distance: 18,
      duration: 520,
      stagger: 70
    },
    {
      selector: '.cta__box',
      distance: 18,
      duration: 560,
      stagger: 0,
      scale: 0.99
    }
  ];

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;

        const element = entry.target;
        observer.unobserve(element);
        observedElements.delete(element);

        const distance = Number(element.dataset.motionDistance || 18);
        const duration = Number(element.dataset.motionDuration || 520);
        const delay = Number(element.dataset.motionDelay || 0);
        const scale = Number(element.dataset.motionScale || 1);
        const animation = element.animate(
          [
            {
              opacity: 0,
              transform: `translate3d(0, ${distance}px, 0) scale(${scale})`
            },
            {
              opacity: 1,
              transform: 'translate3d(0, 0, 0) scale(1)'
            }
          ],
          {
            duration,
            delay,
            easing: 'cubic-bezier(.2,.75,.25,1)',
            fill: 'both'
          }
        );

        activeAnimations.add(animation);
        animation.finished
          .catch(() => {})
          .finally(() => {
            activeAnimations.delete(animation);
            animation.cancel();
          });
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  const registerElement = (element, group, index = 0) => {
    if (handledElements.has(element)) return;
    handledElements.add(element);

    const rect = element.getBoundingClientRect();
    if (rect.top < firstViewportBoundary && rect.bottom > 0) {
      element.dataset.motionReady = 'true';
      return;
    }

    element.dataset.motionDistance = String(group.distance);
    element.dataset.motionDuration = String(group.duration);
    element.dataset.motionDelay = String(Math.min(index * group.stagger, 320));
    element.dataset.motionScale = String(group.scale || 1);
    observedElements.add(element);
    observer.observe(element);
  };

  groups.forEach(group => {
    document.querySelectorAll(group.selector).forEach((element, index) => {
      registerElement(element, group, index);
    });
  });

  // Dynamic or newly added theme sections still receive calm structural
  // reveals even when they do not yet have component-specific selectors. Walk
  // only a few wrapper levels and skip branches that already own motion so the
  // fallback cannot create nested/double animation.
  const fallbackGroup = { distance: 30, duration: 700, stagger: 70, scale: 0.995 };
  const ignoredFallbackTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE']);
  const hasHandledDescendant = element =>
    Array.from(handledElements).some(handled => element !== handled && element.contains(handled));
  const registerUncoveredChildren = (container, depth = 0) => {
    Array.from(container.children).forEach((element, index) => {
      if (ignoredFallbackTags.has(element.tagName) || handledElements.has(element)) return;
      if (hasHandledDescendant(element)) {
        if (depth < 2) registerUncoveredChildren(element, depth + 1);
        return;
      }
      if (element.getBoundingClientRect().height > 0) registerElement(element, fallbackGroup, index);
    });
  };

  document
    .querySelectorAll('main .shopify-section > section, main .shopify-section > article, main > section, main > article')
    .forEach(section => {
      if (section.matches('.hero, .password-shell') || section.closest('.shopify-policy__container')) return;
      const contentRoot = Array.from(section.children).find(element => element.classList.contains('page-width')) || section;
      registerUncoveredChildren(contentRoot);
    });

  const disableMotion = event => {
    if (!event.matches) return;
    observer.disconnect();
    observedElements.clear();
    handledElements.clear();
    activeAnimations.forEach(animation => animation.cancel());
    activeAnimations.clear();
    root.removeAttribute('data-motion-active');
  };

  reduceMotion.addEventListener?.('change', disableMotion);
  root.setAttribute('data-motion-active', 'true');
})();
